import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import type { DuctedPairPick } from 'features/ui/ducted-pair-pick-types';
import type { DuctedBundleSlot } from 'utils/ducted-bundle-picks-types';
import { selectActiveDuctedDesign } from 'utils/ducted-design';
import {
  selectDuctedPairContextBySystemId,
  selectDuctedPairCount
} from 'utils/ducted-pair-context';
import type {
  DuctedPairContext,
  DuctedPairContextState
} from 'utils/ducted-pair-context-types';

export const DUCTED_BUNDLE_MIN_PAIRS = 2;

export function selectDuctedBundleMode(state: DuctedPairContextState): boolean {
  return selectDuctedPairCount(state) >= DUCTED_BUNDLE_MIN_PAIRS;
}

function pairNameFor(
  design: ReturnType<typeof selectActiveDuctedDesign>,
  context: DuctedPairContext
): string {
  const condenser = design?.condensers.find(
    (candidate) => candidate.uniqueId === context.condenserUniqueId
  );
  return condenser?.name || 'Outdoor Unit';
}

export function buildDuctedBundleSlots(
  design: ReturnType<typeof selectActiveDuctedDesign>,
  contexts: Record<string, DuctedPairContext>,
  picks: Record<string, DuctedPairPick>
): DuctedBundleSlot[] {
  return Object.values(contexts).map((context) => ({
    systemUniqueId: context.existingSystemId,
    pairName: pairNameFor(design, context),
    context,
    pick: picks[context.existingSystemId] ?? null
  }));
}

export const selectDuctedBundleSlots = createSelector(
  [
    selectActiveDuctedDesign,
    selectDuctedPairContextBySystemId,
    (state: DuctedPairContextState & Pick<RootState, 'ui'>) =>
      state.ui.ductedPairPicks
  ],
  buildDuctedBundleSlots
);

export function selectDuctedPairName(
  state: DuctedPairContextState,
  systemUniqueId: string | null | undefined
): string | null {
  if (!systemUniqueId) return null;
  const context = selectDuctedPairContextBySystemId(state)[systemUniqueId];
  if (!context) return null;
  return pairNameFor(selectActiveDuctedDesign(state), context);
}

export function isDuctedBundleReady(slots: DuctedBundleSlot[]): boolean {
  return (
    slots.length >= DUCTED_BUNDLE_MIN_PAIRS && slots.every((slot) => slot.pick)
  );
}

export function isPickedForPair(
  picks: Record<string, DuctedPairPick>,
  systemUniqueId: string,
  cardId: string
): boolean {
  return picks[systemUniqueId]?.selection.cardId === cardId;
}
